import { HydratedRouter } from 'react-router/dom';
import { RendererProvider } from '@archipro-design/aria';
import { hydrateRoot } from 'react-dom/client';
import { createFelaRendererFactory } from '../react-northstar-fela-renderer/src/createFelaRenderer';
import { startTransition, useEffect } from 'react';
import type { Logger } from '@archipro-website/logger';
import {
    createRemixClientLogger,
    LoggerProvider,
} from '@archipro-website/logger/client';
import { DebugContextProvider } from './modules/error/context/DebugContext';
import { classifyRouterError } from './modules/error/util/classify-router-error';
import { isLikelyBotOrOutdatedClient } from './modules/error/util/is-likely-bot-client';
import {
    GECKO_NAVIGATION_ABORT_SAMPLE_RATE,
    isGeckoNavigationAbortMessage,
    shouldSampleNavigationAbort,
} from './modules/error/util/navigation-abort';
import { withDocumentNavigationAbortHold } from './modules/error/util/document-navigation';

/**
 * Metadata that React Router v7 passes to `<HydratedRouter onError>` when
 * an error reaches a router-level boundary. The wrapper accepts an optional
 * `errorInfo` (React's `componentStack`) which is only present for the
 * per-route boundary callback path, not for top-level router errors.
 */
type RouterErrorInfo = {
    location?: { pathname?: string; search?: string; hash?: string };
    params?: Record<string, string | undefined>;
    pattern?: string;
    errorInfo?: { componentStack?: string | null };
};

/** Cap a serialised non-Error payload before forwarding it to `/remix-api/log`. */
const truncate = (value: string): string =>
    value.length > 500 ? `${value.slice(0, 500)}...` : value;

/**
 * Coerce whatever `<HydratedRouter onError>` hands us into a real Error so the
 * log carries a usable message. RR7 also surfaces non-Error values here:
 * ErrorResponse-like route errors (`{ status, statusText, data }`), failed
 * dynamic imports, and plain objects. `String({})` is `"[object Object]"`,
 * which told us nothing during the June 2026 flood, so serialise explicitly.
 */
const toLoggableError = (error: unknown): Error => {
    if (error instanceof Error) return error;

    if (
        typeof error === 'object' &&
        error !== null &&
        'status' in error &&
        'statusText' in error
    ) {
        const { status, statusText, data } = error as {
            status?: unknown;
            statusText?: unknown;
            data?: unknown;
        };
        let body = '';
        try {
            body = data === undefined ? '' : truncate(JSON.stringify(data));
        } catch {
            body = String(data);
        }
        return new Error(
            `ErrorResponse ${String(status)} ${String(statusText)}${body ? `: ${body}` : ''}`,
        );
    }

    try {
        const serialised = JSON.stringify(error);
        if (serialised && serialised !== '{}')
            return new Error(truncate(serialised));
    } catch {
        // Circular ref or non-serialisable value - fall through to the key dump.
    }
    const keys =
        typeof error === 'object' && error !== null
            ? ` keys=[${Object.keys(error).join(',')}]`
            : '';
    return new Error(`${String(error)}${keys}`);
};

/**
 * Forward every router-level error to the server-side log endpoint so the
 * first incident on this path is searchable in Datadog. Without this, the
 * error reaches React Router's default boundary (`za` in the runtime
 * chunk), gets `console.error`d, and then `getDerivedStateFromProps`
 * clears the boundary state on the next location change — leaving no
 * structured trace anywhere.
 *
 * `<head>` and `<body>` carry `suppressHydrationWarning` to tolerate
 * third-party head injections (Typekit, GTM, dynamic favicon, browser
 * extensions); that masks legitimate hydration mismatches too, which
 * makes this hook the only place real client-side React errors get
 * captured.
 */
const logRouterError = (
    logger: Logger,
    error: unknown,
    info: RouterErrorInfo = {},
) => {
    const err = toLoggableError(error);
    // Gecko surfaces an aborted prefetch (React Router cancels the loader when
    // the user navigates away) as `NetworkError when attempting to fetch
    // resource.`. This used to be dropped outright to keep Datadog from
    // flooding; that also hid BC-1249 — a broken Firefox error page — for
    // fourteen months. Sample instead, so a regression can still move the line,
    // and keep the console.warn for the events we drop.
    const isGeckoAbort = isGeckoNavigationAbortMessage(err.message);
    if (isGeckoAbort && !shouldSampleNavigationAbort()) {
        console.warn('[router] Gecko prefetch abort (sampled out)', err);
        return;
    }
    const { location, params, pattern, errorInfo } = info;
    // Log only the pathname and route pattern. The search string and resolved
    // param values can carry secrets (invite hashes, reset tokens), so keep them
    // out of `/remix-api/log`. The pattern (e.g. `/invite/:token`) is enough to
    // attribute the error to a route.
    const routerLocation =
        typeof location?.pathname === 'string' ? location.pathname : undefined;
    const routerParamKeys =
        params && typeof params === 'object'
            ? Object.keys(params).sort()
            : undefined;
    // Append a derived category to the message (and tag it structurally) so
    // Datadog can split real RR7 bugs (routeId/turbo-stream/chunk load) from
    // bot/network/auth noise without drilling into `@error.message`.
    const routerErrorCategory = classifyRouterError(err);
    // Router errors from bots and abandoned browsers (predominantly end-of-life
    // Chrome that cannot execute the RR7 single-fetch path) are not actionable
    // real-user bugs. Tag them and log below `error` so they do not pollute the
    // actionable signal; current-browser errors stay at `error` and remain
    // alertable, so a genuine RR7 regression still surfaces.
    const outdatedClient = isLikelyBotOrOutdatedClient(
        navigator.userAgent,
        navigator.webdriver,
    );
    const message = `React Router client error boundary catch: ${routerErrorCategory}`;
    const meta = {
        routerErrorCategory,
        routerErrorOutdatedClient: outdatedClient,
        // Present only on the sampled Gecko aborts, so a Datadog count can be
        // scaled back to the true volume instead of read as a raw total.
        routerErrorSampleRate: isGeckoAbort
            ? GECKO_NAVIGATION_ABORT_SAMPLE_RATE
            : undefined,
        routerLocation,
        routerPattern: pattern,
        routerParamKeys,
        componentStack: errorInfo?.componentStack ?? undefined,
    };
    if (outdatedClient) {
        logger.warning(message, err, meta);
    } else {
        logger.error(message, err, meta);
    }
};

// https://github.com/facebook/react/issues/24430#issuecomment-1156537554
document.querySelectorAll('html > script').forEach((s) => {
    s?.parentNode?.removeChild(s);
});

// Installed before hydration so every React Router loader and fetcher goes
// through it: requests cancelled by our own full-page redirects must not reach
// the error boundary.
window.fetch = withDocumentNavigationAbortHold(window.fetch.bind(window));

const { create: createFelaRenderer } = createFelaRendererFactory();
const renderer = createFelaRenderer();

/**
 * True until the initial hydration has committed. React only reports
 * recoverable errors during hydration (mismatches that switch the root to
 * client rendering — React #418/#423/#425); once this root effect runs the
 * commit is done and we leave the window. A root-level effect is the reliable
 * signal here — effects run strictly after the associated commit, so it can't
 * flip before a hydration-time recoverable error has fired.
 */
let isHydrating = true;
const HydrationComplete = () => {
    useEffect(() => {
        isHydrating = false;
    }, []);
    return null;
};

// React reports hydration recoverable errors as a batch within one commit;
// resetting Fela once per batch is enough (reset re-emits the full cache).
let hasResetRenderer = false;

const hydrate = () => {
    startTransition(() => {
        const logger = createRemixClientLogger();
        hydrateRoot(
            document,
            <LoggerProvider logger={logger}>
                <RendererProvider value={() => renderer}>
                    <DebugContextProvider>
                        <HydrationComplete />
                        <HydratedRouter
                            onError={(error, info) =>
                                logRouterError(
                                    logger,
                                    error,
                                    info as RouterErrorInfo,
                                )
                            }
                        />
                    </DebugContextProvider>
                </RendererProvider>
            </LoggerProvider>,
            {
                onRecoverableError: (error, errorInfo) => {
                    // A custom handler suppresses React's default console
                    // reporting, so log here — otherwise a hydration-mismatch
                    // regression would ship with zero Datadog/console signal.
                    logger.warning(
                        'Recoverable render error',
                        toLoggableError(error),
                        {
                            isHydrating,
                            componentStack:
                                errorInfo?.componentStack ?? undefined,
                        },
                    );
                    /**
                     * Fela-specific recovery: when React falls back to full
                     * client rendering after a hydration miss, the renderer
                     * needs a reset so the second render emits the full
                     * stylesheet again. We reset on any recoverable error that
                     * fires inside the hydration window rather than matching
                     * minified React error strings, which drift across React
                     * versions; post-hydration errors are left alone so a
                     * mid-session reset can't flash the stylesheet.
                     */
                    if (isHydrating && !hasResetRenderer) {
                        hasResetRenderer = true;
                        renderer.reset();
                    }
                },
            },
        );
    });
};

if (window.requestIdleCallback) {
    window.requestIdleCallback(hydrate);
} else {
    // Safari doesn't support requestIdleCallback
    // https://caniuse.com/requestidlecallback
    window.setTimeout(hydrate, 1);
}
